import type {
  PlayerChatConversation,
  PlayerChatHistory,
  PlayerChatMessage,
} from '../api/public.ts'
import { compareDecimalIDs } from './playerSocialPresentation.ts'

type ChatHistoryReader = (
  conversationID: string,
  chatEpoch: string,
  options?: { limit?: number; beforeID?: string; afterID?: string },
) => Promise<PlayerChatHistory>

export type PlayerChatWindow = {
  messages: PlayerChatMessage[]
  prunedThroughMessageID: string
  replaceHistory: boolean
  epochChanged: boolean
}

type LoadPlayerChatWindowOptions = {
  nextConversation: PlayerChatConversation
  currentConversation: PlayerChatConversation | null
  forceReplace?: boolean
  readHistory: ChatHistoryReader
  isCurrent?: () => boolean
}

// Loads the complete bounded reconciliation window for one conversation.
// Transport paging lives here so the social-dock coordinator does not need to
// duplicate the two-page 200-message protocol for full and addressed refreshes.
export async function loadPlayerChatWindow(
  options: LoadPlayerChatWindowOptions,
): Promise<PlayerChatWindow | null> {
  const {
    nextConversation,
    forceReplace = false,
    readHistory,
    isCurrent = () => true,
  } = options
  const epochChanged = Boolean(
    options.currentConversation &&
      (
        options.currentConversation.id !== nextConversation.id ||
        options.currentConversation.chat_epoch !== nextConversation.chat_epoch
      ),
  )
  const currentConversation = epochChanged ? null : options.currentConversation
  const replaceHistory = (
    forceReplace ||
    !currentConversation ||
    currentConversation.last_message_id === '0'
  )
  let messages: PlayerChatMessage[] = []
  let prunedThroughMessageID = '0'

  if (replaceHistory) {
    const latest = await readHistory(
      nextConversation.id,
      nextConversation.chat_epoch,
      { limit: 100 },
    )
    if (!isCurrent()) return null
    messages = latest.messages
    prunedThroughMessageID = maxDecimalID(
      prunedThroughMessageID,
      latest.pruned_through_message_id,
    )
    if (latest.has_more && latest.next_before_id) {
      const older = await readHistory(
        nextConversation.id,
        nextConversation.chat_epoch,
        {
          limit: 100,
          beforeID: latest.next_before_id,
        },
      )
      if (!isCurrent()) return null
      messages = [...older.messages, ...messages]
      prunedThroughMessageID = maxDecimalID(
        prunedThroughMessageID,
        older.pruned_through_message_id,
      )
    }
  } else if (
    compareDecimalIDs(
      nextConversation.last_message_id,
      currentConversation.last_message_id,
    ) > 0
  ) {
    const newer = await readHistory(
      nextConversation.id,
      nextConversation.chat_epoch,
      {
        limit: 100,
        afterID: currentConversation.last_message_id,
      },
    )
    if (!isCurrent()) return null
    messages = newer.messages
    prunedThroughMessageID = maxDecimalID(
      prunedThroughMessageID,
      newer.pruned_through_message_id,
    )
    if (newer.has_more && newer.next_after_id) {
      const newest = await readHistory(
        nextConversation.id,
        nextConversation.chat_epoch,
        {
          limit: 100,
          afterID: newer.next_after_id,
        },
      )
      if (!isCurrent()) return null
      messages = [...messages, ...newest.messages]
      prunedThroughMessageID = maxDecimalID(
        prunedThroughMessageID,
        newest.pruned_through_message_id,
      )
    }
  }

  return {
    messages,
    prunedThroughMessageID,
    replaceHistory,
    epochChanged,
  }
}

function maxDecimalID(left: string, right: string): string {
  return compareDecimalIDs(left, right) >= 0 ? left : right
}
